/** @jsxImportSource @emotion/react */
import React from "react";
import {
  Box,
  Typography,
  Grid,
  TextField,
  FormControl,
  Select,
  MenuItem,
  FormHelperText,
  Divider,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { styled } from "@mui/material/styles";
import { useEventData } from "../EventDataContext";

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#FFFFFF",
    borderRadius: "12px",
    fontSize: "15px",
    fontWeight: 400,
    color: "#333",
    transition: "all 0.2s ease-in-out",
    "& fieldset": {
      border: "1px solid #E0E0E0",
    },
    "&:hover fieldset": {
      border: "1px solid #BDBDBD",
    },
    "&.Mui-focused fieldset": {
      border: "1px solid #8A5918",
      boxShadow: "0 0 0 3px rgba(138, 89, 24, 0.1)",
    },
    "& .MuiOutlinedInput-input": {
      padding: "12px 16px",
      "&::placeholder": {
        color: "#9CA3AF",
        opacity: 1,
      },
    },
  },
  "& .MuiInputLabel-root": {
    color: "#666",
    fontSize: "15px",
    "&.Mui-focused": {
      color: "#8A5918",
    },
  },
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#FFFFFF",
    borderRadius: "12px",
    fontSize: "15px",
    fontWeight: 400,
    color: "#333",
    transition: "all 0.2s ease-in-out",
    "& fieldset": {
      border: "1px solid #E0E0E0",
    },
    "&:hover fieldset": {
      border: "1px solid #BDBDBD",
    },
    "&.Mui-focused fieldset": {
      border: "1px solid #8A5918",
      boxShadow: "0 0 0 3px rgba(138, 89, 24, 0.1)",
    },
    "& .MuiSelect-select": {
      padding: "12px 16px",
    },
  },
  "& .MuiInputLabel-root": {
    color: "#666",
    fontSize: "15px",
    "&.Mui-focused": {
      color: "#8A5918",
    },
  },
}));

const SectionTitle = ({ children }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 1 }}>
    <Box sx={{ width: '4px', height: '24px', backgroundColor: '#8A5918', mr: 1, borderRadius: '2px' }} />
    <Typography
      variant="h6"
      sx={{
        color: "#1A1A1A",
        fontWeight: 700,
        fontSize: "18px",
        textAlign: "left",
      }}
    >
      {children}
    </Typography>
  </Box>
);

const Label = ({ children, required }) => (
  <Typography
    sx={{
      color: "#4A4A4A",
      fontWeight: 600,
      fontSize: "14px",
      mb: 1,
      textAlign: "left",
    }}
  >
    {children} {required && <span style={{ color: "#E94C4C" }}>*</span>}
  </Typography>
);

export default function InformationPage() {
  const { eventData, updateEventData, validationErrors } = useEventData();
  const information = eventData.information;

  const handleInputChange = (field, value) => {
    updateEventData("information", {
      ...information,
      [field]: { value, required: true },
    });
  };

  const getStringValue = (field) => {
    return field?.value || "";
  };

  const getDateValue = (field) => {
    return field?.value ? new Date(field.value) : null;
  };

  const hasError = (fieldName) => {
    return !!validationErrors?.information?.[fieldName];
  };

  const getErrorMessage = (fieldName) => {
    return validationErrors?.information?.[fieldName] || "";
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <SectionTitle>Event Details</SectionTitle>
        <Grid container spacing={3}>
          {/* Event Name */}
          <Grid item xs={12}>
            <Label required>Event Name</Label>
            <StyledTextField
              fullWidth
              placeholder="e.g. Wedding of John & Jane"
              value={getStringValue(information?.eventName)}
              onChange={(e) => handleInputChange("eventName", e.target.value)}
              error={hasError("eventName")}
              helperText={getErrorMessage("eventName")}
            />
          </Grid>

          {/* Event Type */}
          <Grid item xs={12} md={12}>
            <Label required>Event Type</Label>
            <StyledFormControl fullWidth error={hasError("eventType")}>
              <Select
                value={getStringValue(information?.eventType)}
                onChange={(e) => handleInputChange("eventType", e.target.value)}
                displayEmpty
                sx={{
                  "& .MuiSelect-select": {
                    padding: "12px 16px",
                  },
                }}
              >
                <MenuItem value="" disabled>
                  Select Type
                </MenuItem>
                <MenuItem value="wedding">Wedding</MenuItem>
                <MenuItem value="birthday">Birthday</MenuItem>
                <MenuItem value="engagement">Engagement</MenuItem>
                <MenuItem value="anniversary">Anniversary</MenuItem>
                <MenuItem value="party">Party</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
              {hasError("eventType") && (
                <FormHelperText>{getErrorMessage("eventType")}</FormHelperText>
              )}
            </StyledFormControl>
          </Grid>
          {/* Event Date */}
          <Grid item xs={12} md={12}>
            <Label required>Event Date</Label>
            <DatePicker
              value={getDateValue(information?.eventDate)}
              onChange={(newValue) => handleInputChange("eventDate", newValue)}
              format="dd/MM/yyyy"
              enableAccessibleFieldDOMStructure={false}
              slots={{ textField: StyledTextField }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  placeholder: "Select date",
                  error: hasError("eventDate"),
                  helperText: getErrorMessage("eventDate"),
                },
              }}
            />
          </Grid>

          {/* Event Status */}
          <Grid item xs={12} md={12}>
            <Label required>Event Status</Label>
            <StyledFormControl fullWidth error={hasError("eventStatus")}>
              <Select
                value={getStringValue(information?.eventStatus)}
                onChange={(e) => handleInputChange("eventStatus", e.target.value)}
                displayEmpty
              >
                <MenuItem value="" disabled>
                  Select Status
                </MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="prepared">Prepared</MenuItem>
                <MenuItem value="publish">Publish</MenuItem>
                <MenuItem value="unpublished">Unpublished</MenuItem>
              </Select>
              {hasError("eventStatus") && (
                <FormHelperText>{getErrorMessage("eventStatus")}</FormHelperText>
              )}
            </StyledFormControl>
          </Grid>
        </Grid>

        <Box sx={{ my: 4 }}>
          <Divider />
        </Box>

        <SectionTitle>Location & Schedule</SectionTitle>
        <Grid container spacing={3}>
          {/* Location Name */}
          <Grid item xs={12} md={12}>
            <Label required>Location Name</Label>
            <StyledTextField
              fullWidth
              placeholder="e.g. Grand Hotel Ballroom"
              value={getStringValue(information?.locationName)}
              onChange={(e) => handleInputChange("locationName", e.target.value)}
              error={hasError("locationName")}
              helperText={getErrorMessage("locationName")}
            />
          </Grid>

          {/* Google Maps Link */}
          <Grid item xs={12} md={12}>
            <Label required>Google Maps Link</Label>
            <StyledTextField
              fullWidth
              placeholder="https://maps.google.com/..."
              value={getStringValue(information?.googleMapsLink)}
              onChange={(e) =>
                handleInputChange("googleMapsLink", e.target.value)
              }
              error={hasError("googleMapsLink")}
              helperText={getErrorMessage("googleMapsLink")}
            />
          </Grid>

          {/* Start Time */}
          <Grid item xs={12} md={12}>
            <Label required>Start Time</Label>
            <StyledTextField
              fullWidth
              type="time"
              value={getStringValue(information?.startTime)}
              onChange={(e) => handleInputChange("startTime", e.target.value)}
              error={hasError("startTime")}
              helperText={getErrorMessage("startTime")}
            />
          </Grid>

          {/* End Time */}
          <Grid item xs={12} md={12}>
            <Label required>End Time</Label>
            <StyledTextField
              fullWidth
              type="time"
              value={getStringValue(information?.endTime)}
              onChange={(e) => handleInputChange("endTime", e.target.value)}
              error={hasError("endTime")}
              helperText={getErrorMessage("endTime")}
            />
          </Grid>
        </Grid>
      </Box>
    </LocalizationProvider>
  );
}
